import * as React from 'react';
import { RouteComponentProps, withRouter, Link } from 'react-router-dom';
import { Button, Card, Col, Divider, Modal, Row, Table, Input, notification } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

import CategoriesQuery from '../../components/Query/Categories';
import buildTree from '../../helpers/tree';
import CategoryDetail from './CategoryDetail';
import * as _ from 'lodash';
import { Title } from '../../components/Layout/components';
import NotFoundPage from '../NotFound/NotFound';

interface ICategoriesTableProps extends RouteComponentProps {
  domain: string;
  categoryId?: string;
  parentCategoryId?: string;
}

interface CategoryTableState {
  searchQuery: string;
}

class CategoriesTable extends React.Component<ICategoriesTableProps, CategoryTableState> {

  constructor(props: any) {
    super(props);

    this.state = {
      searchQuery: '',
    };

    this.renderActions = this.renderActions.bind(this);
  }

  public render() {
    return (
      <Row>
        <Col span={24}>
          <CategoriesQuery
            search={this.state.searchQuery}
            domain={this.props.domain}
          >
            {({ categories, createCategory, updateCategory, deleteCategory }: any) => {
              return this.renderData(categories, createCategory, updateCategory, deleteCategory, categories.refetch);
            }}
          </CategoriesQuery>
        </Col>
      </Row>
    );
  }

  private renderData(
    categories: any,
    createCategory: Function,
    updateCategory: Function,
    deleteCategory: Function,
    refetch: Function
  ) {
    if (categories.categories && !categories.loading) {

      let tempCategories = categories.categories.map((c: GQL.ICategory) => {
        return {
          ...c,
          parentId: c.parentCategory ? c.parentCategory.id : undefined
        };
      });

      // formulář
      if (this.props && this.props.categoryId) {
        const selectedCategory = categories.categories.find((c: GQL.ICategory) => c.id === this.props.categoryId) || undefined;
        if (this.props.categoryId !== 'new' && !selectedCategory) {
          return <NotFoundPage domain={this.props.domain} />
        }

        return (
          <CategoryDetail
            domain={this.props.domain}
            parentCategoryId={this.props.parentCategoryId}
            languages={categories.languages}
            category={selectedCategory || {}}
            onSave={(category) => this.upsert(category, createCategory, updateCategory, refetch)}
            onCancel={() => this.cancel()}
          />
        );
      }

      tempCategories = _.orderBy(tempCategories, ['priority']);

      // tabulka kategorií, při vyhledávání klasický seznam, jinak strom
      const categoryTree = this.state.searchQuery !== ''
        ? [...tempCategories]
        : buildTree(tempCategories, 'parentId', 'id');

      return (
        <div>
          <Title>Categories</Title>
          <Divider />
          <Row>
            <Col span={12}>
              <Link className="ant-btn" to={this.getLink('new')}>Create new category</Link>
            </Col>
            <Col span={12}>
              <Input.Search
                placeholder="Search categories..."
                defaultValue={this.state.searchQuery}
                onSearch={query => this.changeSearchQuery(query)}
                enterButton={true}
              />
            </Col>
          </Row>
          <Table
            rowKey={(record: GQL.ICategory) => record.id}
            dataSource={categoryTree}
            pagination={false}
            defaultExpandAllRows={true}
            style={{ marginTop: '20px' }}
          >
            <Table.Column
              title="Category"
              dataIndex="title"
              key="title"
              render={(u, row: GQL.ICategory) => {
                return row.name['en'] || row.title;
              }}
            />
            <Table.Column
              title="Priority"
              dataIndex="priority"
              key="priority"
              width={100}
              align="center"
            />
            <Table.Column
              title="Active"
              dataIndex="active"
              key="active"
              width={100}
              align="center"
              render={(u, row: GQL.IPaymentMethod) => {
                return (
                  <span>
                    {row.active ? (
                      <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                    ) : (
                        <CloseOutlined style={{ fontSize: 20 }} />
                      )}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Actions"
              dataIndex="actions"
              key="actions"
              width={290}
              align="center"
              render={(u: any, row: any) =>
                this.renderActions(u, categories.categories, row, deleteCategory, refetch)
              }
            />
          </Table>
        </div>
      );
    }

    return (
      <div>
        <Divider />
        <Card loading={true} title="Loading categories ..." style={{ width: '100%' }} />
      </div>
    );
  }

  private renderActions(
    u: any,
    allCategories: Array<GQL.ICategory>,
    category: any,
    deleteCategory: Function,
    refetch: Function
  ) {
    return (
      <div>
        <Link className="ant-btn" type={'dashed'} to={this.getLink('new', category.id)}>
          Add child
        </Link>
        <Divider type={'vertical'} />
        <Link className="ant-btn" type={'dashed'} to={this.getLink(category.id)}>
          Edit
        </Link>
        <Divider type={'vertical'} />
        <Button
          danger={true}
          disabled={allCategories.some(c => (c.parentCategory && c.parentCategory.id) == category.id)}
          onClick={() => this.delete(category, deleteCategory, refetch)}
        >
          Delete
        </Button>
      </div>
    );
  }

  private changeSearchQuery(searchQuery: string) {
    this.setState({
      searchQuery
    });
  }

  private delete(category: GQL.ICategory, deleteCategory: Function, refetch: Function) {
    Modal.confirm({
      title: `Delete ${category.title}?`,
      content: (
        <span>
          Are you sure you want to delete category <strong>{category.title}</strong>?
        </span>
      ),
      onOk: () => {
        return deleteCategory({
          variables: {
            id: category.id
          }
        }).then(() => refetch());
      }
    });
  }

  private upsert(
    category: GQL.ICategory,
    createCategory: Function,
    updateCategory: Function,
    refetch: Function
  ) {
    if (category) {
      const variables = {
        id: category.id,
        category: {
          parentId: category.parentCategory
            ? category.parentCategory.id
            : this.props.parentCategoryId,
          title: category.title,
          name: category.name,
          longName: category.longName,
          priority: category.priority,
          slug: category.slug,
          description: category.description || {},
          longDescription: category.longDescription || {},
          heurekaCategory: category.heurekaCategory,
          image: category.image,
          banner: category.banner || {},
          bannerLink: category.bannerLink || {},
          active: category.active !== undefined ? category.active : true,
          attributes: (category.attributes || []).map(attr => attr.id),
          metaTitle: category.metaTitle,
          metaDescription: category.metaDescription,
        }
      };

      if (category.id) {
        return updateCategory({ variables })
          .then(() => refetch())
          .then(() => this.cancel())
          .then(() =>
            notification['success']({
              message: 'Info',
              description: `Category "${variables.category.title}" has been updated.`
            })
          );
      }

      (variables.category as any).domain = this.props.domain;

      createCategory({ variables })
        .then(() => refetch())
        .then(() => this.cancel())
        .then(() =>
          notification['success']({
            message: 'Info',
            description: `Category "${variables.category.title}" has been created.`
          })
        );
    }
  }

  private getLink(recordId: string | null, parentCategoryId: string | null = null) {
    return `/${this.props.domain}/application/${recordId
      ? `category/${recordId}${parentCategoryId ? `/${parentCategoryId}` : ``}`
      : 'category'}`;
  }

  private cancel() {
    this.props.history.push(this.getLink(null));
  }
}

export default withRouter<ICategoriesTableProps>(CategoriesTable);



// WEBPACK FOOTER //
// ./src/scenes/Categories/CategoriesTable.tsx